import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import Icon, { IconButton } from '../components/Icon';
import MindMap from '../components/MindMap';

/* The assistant.

   Two things drive the shape of this screen:

   1. There is no streaming — Aegis has no SSE yet, so a reply lands whole.
      A spinner that sits still for twenty seconds reads as broken, so the
      waiting state says what is actually happening and escalates its wording
      once a cold model start becomes the likely explanation.
   2. Deleting a conversation really deletes it, so it asks first. */

const COLD_START_MS = 6000;
const MINDMAP_MARK = '::mindmap::';
const IMAGE_MARK = '::image::';
const FOUND_MARK = '::found::';

/* What Copilot found when asked "find the post where…": its answer, then the
   matches as cards that open the post or group. */
function Found({ body, onNavigate }) {
  let data = { answer: '', hits: [] };
  try { data = JSON.parse(body.slice(FOUND_MARK.length)); } catch { /* shown as empty */ }
  const label = { post: 'Post', comment: 'Comment', group_message: 'Group' };
  return (
    <div className="found">
      <Rich text={data.answer} />
      <ol className="found-hits">
        {data.hits.map((h) => (
          <li key={`${h.type}-${h.id}`}>
            <button type="button" className="found-hit" onClick={() => (h.type === 'group_message'
              ? onNavigate?.('groups', { group: h.group_id, topic: h.topic_id })
              : onNavigate?.('post', { post: h.post_id }))}>
              <span className="found-meta">{label[h.type] || 'Item'} · {h.by} · {h.at ? new Date(h.at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : ''}</span>
              <b>{h.title}</b>
              <span>{h.snippet}</span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}
// "Create an image of …", "draw me …" — the server decides; this only picks the
// waiting message.
const IMAGE_ASK_RE = /^\s*(please\s+|can you\s+|could you\s+)?((create|make|generate|design|render|paint|give me)\b.{0,40}?\b(image|picture|pic|photo|illustration|drawing|artwork|art|poster|wallpaper|portrait|logo|icon|sketch|banner|visual)s?\b|(draw|sketch|paint|illustrate)\s+(me|a|an|the)\b)/i;

/* One-click document tools — the NotebookLM set. Podcast is a placeholder. */
/* Hidden until members ask for it (2026-10-26). Flip to bring the placeholder back. */
const SHOW_PODCAST = false;
const DOC_TOOLS = [
  ['summary', 'Summary', 'summary'],
  ['outline', 'Outline', 'outline'],
  ['faq', 'FAQ', 'faq'],
  ['timeline', 'Timeline', 'timeline'],
  ['watchouts', 'Watch-outs', 'warning'],
  ['mindmap', 'Mind map', 'mindmap'],
];

/* **bold** is the only formatting models reliably use; show it as bold rather
   than as asterisks. */
function Rich({ text }) {
  return (text || '').split(/(\*\*[^*]+\*\*)/g).map((part, i) => (
    part.startsWith('**') && part.endsWith('**')
      ? <b key={i}>{part.slice(2, -2)}</b>
      : <React.Fragment key={i}>{part}</React.Fragment>
  ));
}

/* Pick one of your files, or upload a new one, to talk about. */
const IMAGE_RE = /\.(png|jpe?g|webp)$/i;

function AttachMenu({ onPick, onUpload, onClose, heading, documentsOnly = false }) {
  const [files, setFiles] = useState(null);
  const input = useRef(null);
  useEffect(() => {
    api.files()
      .then((d) => setFiles(d.files.filter((f) => f.status === 'ready'
        && !(documentsOnly && f.content_type.startsWith('image/')))))
      .catch(() => setFiles([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="attach-menu" role="dialog" aria-label={heading || 'Attach a file'}>
      {heading && <p className="attach-heading">{heading}</p>}
      <button type="button" className="attach-upload" onClick={() => input.current?.click()}>
        <Icon name="upload" size={18} /> {documentsOnly ? 'Upload a document…' : 'Upload a file or image…'}
      </button>
      <input
        ref={input} type="file" hidden
        accept={documentsOnly ? '.pdf,.docx,.txt,.md' : '.pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.webp'}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onUpload(f); }}
      />
      {files === null && <p className="muted-sm">Loading your files…</p>}
      {files?.length > 0 && <p className="attach-label">Or talk about one you've uploaded</p>}
      {files?.map((f) => (
        <button type="button" key={f.id} className="attach-file" onClick={() => onPick(f.id)}>
          <Icon name={IMAGE_RE.test(f.filename) ? 'image' : 'file'} size={18} /> <span>{f.filename}</span>
        </button>
      ))}
      <button type="button" className="linklike attach-cancel" onClick={onClose}>Cancel</button>
    </div>
  );
}

/* An image Copilot made. Private, so it's fetched with the member's token. */
function GeneratedImage({ fileId, prompt, onRetry, busy }) {
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let made = '';
    api.imageUrl(fileId).then((u) => { made = u; setUrl(u); }).catch(() => setFailed(true));
    return () => { if (made) URL.revokeObjectURL(made); };
  }, [fileId]);
  return (
    <figure className="gen-image">
      {url ? <img src={url} alt={prompt} />
        : <div className="gen-image-ph">{failed ? 'This image was deleted from your files.' : 'Loading image…'}</div>}
      <figcaption className="muted-sm">{prompt}</figcaption>
      <div className="gen-image-actions">
        {url && <button type="button" className="btn quiet sm"
          onClick={() => api.downloadFile(fileId, 'copilot-image.webp')}>
          <Icon name="download" size={14} /> Download</button>}
        <button type="button" className="btn quiet sm" disabled={busy} onClick={() => onRetry(prompt)}>Try again</button>
      </div>
      <p className="muted-sm gen-image-note">Saved to your files · AI images can get details wrong</p>
    </figure>
  );
}

function Waiting({ preset, image }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), COLD_START_MS);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="assistant-msg assistant-msg-pending">
      <span className="assistant-dots" aria-hidden="true"><i /><i /><i /></span>
      <span className="assistant-wait-text">
        {image
          ? (slow ? 'Still painting — the first image after a quiet spell takes up to a minute.' : 'Creating your image…')
          : slow
          ? 'Still going — the model is waking up. This is only slow the first time.'
          : preset === 'thinking'
            ? 'Thinking this through…'
            : 'Thinking…'}
      </span>
    </div>
  );
}

/* A thing the assistant has offered to do. Nothing has happened yet — the
   card is the confirmation step, and it is the only way anything gets written. */
function ActionCard({ action, onApply, onDismiss, busy }) {
  const [custom, setCustom] = useState('');
  const [editing, setEditing] = useState(false);
  const options = action.options || [];
  const fields = action.fields || [];

  if (action.kind === 'navigate') return null;

  // Several fields drafted from one paste. Shown in full rather than summarised:
  // this is the only chance to see what would be written before it is.
  if (fields.length > 0) {
    return (
      <div className="action-card">
        <span className="action-field">{action.summary}</span>
        <dl className="action-fields">
          {fields.map((f) => (
            <div key={f.key}>
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
        <div className="action-buttons">
          <button className="btn primary sm" disabled={busy} onClick={() => onApply(action, {})}>
            Save all
          </button>
          <button className="btn quiet sm" disabled={busy} onClick={() => onDismiss(action)}>
            Skip — ask me one at a time
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`action-card${action.irreversible ? ' is-irreversible' : ''}`}>
      {action.field_label && <span className="action-field">{action.field_label}</span>}

      {options.length > 0 && !editing && (
        <ul className="action-options">
          {options.map((option, i) => (
            <li key={option}>
              <button type="button" disabled={busy} onClick={() => onApply(action, { choice: i })}>
                <span className="action-option-text">{option}</span>
                <span className="action-option-cta">Use this</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {options.length === 0 && !editing && <p className="action-summary">{action.summary}</p>}

      {editing && (
        <textarea
          className="action-edit"
          rows={3}
          autoFocus
          value={custom}
          placeholder="Write it your way…"
          onChange={(e) => setCustom(e.target.value)}
        />
      )}

      {action.consequence && <p className="action-consequence">{action.consequence}</p>}

      <div className="action-buttons">
        {editing ? (
          <>
            <button
              className="btn primary sm"
              disabled={busy || !custom.trim()}
              onClick={() => onApply(action, { custom: custom.trim() })}
            >
              Save mine
            </button>
            <button className="btn quiet sm" disabled={busy} onClick={() => setEditing(false)}>
              Back
            </button>
          </>
        ) : (
          <>
            {options.length === 0 && (
              <button className="btn primary sm" disabled={busy} onClick={() => onApply(action, {})}>
                {action.irreversible ? 'Yes, do it' : 'Save'}
              </button>
            )}
            {/* An action Copilot would carry out runs as drafted — there's nothing of yours to rewrite. */}
            {!action.kind?.startsWith('cap:') && (
              <button className="btn quiet sm" disabled={busy} onClick={() => setEditing(true)}>
                Write my own
              </button>
            )}
            <button className="btn quiet sm" disabled={busy} onClick={() => onDismiss(action)}>
              Skip
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* Copilot. Opens as a panel over whatever page you are on, from the one ✦ in
   the menu — so asking it to help with your profile leaves the profile in view,
   and closing it leaves you exactly where you were. */
export default function Assistant({ notify, onNavigate, onApplied, onClose, fileRequest }) {
  const [presets, setPresets] = useState([]);
  const [preset, setPreset] = useState('fast');
  const [conversations, setConversations] = useState([]);
  const [current, setCurrent] = useState(null);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [action, setAction] = useState(null);
  const [applying, setApplying] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [attaching, setAttaching] = useState(false);
  // A tool pressed before any file was attached: it runs once one is chosen.
  const [pendingTool, setPendingTool] = useState(null);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [working, setWorking] = useState('');
  // The Create image button: the next message is a picture to make.
  const [imageMode, setImageMode] = useState(false);
  const [pendingImage, setPendingImage] = useState(false);
  const [imagesOn, setImagesOn] = useState(false);
  // The conversation on screen *now*. An upload takes seconds; by the time it
  // finishes the member may have started a conversation, and the file must join
  // that one rather than replace it with an empty one.
  const currentId = useRef(null);
  useEffect(() => { currentId.current = current?.id || null; }, [current]);
  const endRef = useRef(null);

  useEffect(() => {
    api.presets().then(setPresets).catch(() => {});
    api.assistantFeatures().then((f) => setImagesOn(Boolean(f.images))).catch(() => {});
    refreshList();
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [current, pending, action]);

  function refreshList() {
    api.conversations().then(setConversations).catch(() => {});
  }

  function open(id) {
    setError('');
    setAction(null);
    setShowHistory(false);
    api.conversation(id).then((c) => { setCurrent(c); setPreset(c.preset); }).catch(() => {});
  }

  function startNew() {
    setShowHistory(false);
    setCurrent(null);
    setError('');
    setDraft('');
    setAction(null);
  }

  async function applyAction(a, body) {
    setApplying(true);
    try {
      const res = await api.applyAction(a.id, body);
      setAction(null);
      notify?.(res.result);
      // Whatever it changed (usually the profile) should show on the page behind.
      onApplied?.();
      // Saving mid-interview rolls straight into the next question rather than
      // making the member ask again.
      if (res.next_message) {
        setCurrent((c) => (c ? { ...c, messages: [...c.messages, res.next_message] } : c));
      }
    } catch (err) {
      notify?.(err.message);
    } finally {
      setApplying(false);
    }
  }

  async function dismissAction(a) {
    try {
      await api.dismissAction(a.id);
    } catch {
      /* dismissing is best-effort — the card goes either way */
    }
    setAction(null);
  }

  async function remove(id, event) {
    event.stopPropagation();
    if (!window.confirm('Delete this conversation? The messages are gone for good.')) return;
    try {
      await api.deleteConversation(id);
      if (current?.id === id) setCurrent(null);
      refreshList();
      notify?.('Conversation deleted');
    } catch (err) {
      notify?.(err.message);
    }
  }

  async function send(event) {
    event.preventDefault();
    await sendText(draft.trim(), imageMode);
  }

  async function sendText(body, asImage = false) {
    if (!body || pending || working) return;
    const wantsImage = asImage || IMAGE_ASK_RE.test(body);

    // Show the member's own message immediately — waiting for the round trip to
    // echo it back makes the whole screen feel stalled.
    const optimistic = { id: `local-${Date.now()}`, role: 'user', body, created_at: '' };
    setCurrent((c) =>
      c ? { ...c, messages: [...c.messages, optimistic] } : { id: null, title: body, preset, messages: [optimistic] }
    );
    setDraft('');
    setImageMode(false);
    setPending(true);
    setPendingImage(wantsImage && !file);
    setError('');
    setAction(null);

    try {
      const res = await api.ask(body, preset, current?.id || undefined, asImage);
      const detail = await api.conversation(res.conversation_id);
      setCurrent(detail);
      refreshList();

      // Navigation has no side effect worth confirming, so it just happens.
      if (res.action?.kind === 'navigate' && res.action.view) {
        // Where inside the page too: a profile field, or one conversation.
        onNavigate?.(res.action.view, {
          focus: res.action.focus || undefined,
          thread: res.action.thread_id || undefined,
        });
      } else if (res.action) {
        setAction(res.action);
      }
    } catch (err) {
      setError(err.message);
      // Put the text back so nothing is lost to a failed send.
      setDraft(body);
      setImageMode(asImage);
      setCurrent((c) =>
        c ? { ...c, messages: c.messages.filter((m) => m.id !== optimistic.id) } : null
      );
    } finally {
      setPending(false);
      setPendingImage(false);
    }
  }

  const messages = current?.messages || [];
  const file = current?.file || null;

  async function attach(fileId) {
    setAttaching(false);
    setError('');
    setWorking('Opening your file…');
    try {
      const detail = await api.attachFile(fileId, currentId.current);
      setCurrent(detail);
      refreshList();
      afterAttach(detail);
    } catch (err) {
      setError(err.message);
    } finally {
      setWorking('');
    }
  }

  async function uploadAndAttach(f) {
    setAttaching(false);
    setError('');
    setWorking(`Reading ${f.name}…`);
    try {
      // Files brought into a chat are private and kept out of matching: a
      // contract is not a profile.
      const saved = await api.uploadFile(f, { purpose: 'other', visibility: 'private', useForMatching: false });
      // A pending tool needs text; an image can't be summarised or outlined.
      if (pendingTool && IMAGE_RE.test(saved.filename)) {
        setPendingTool(null);
        throw new Error('Those tools work on documents, not images. Attach a PDF or document to use them.');
      }
      if (saved.status !== 'ready') throw new Error(
        saved.status === 'no_text'
          ? 'There is no readable text in that file — it is probably a scanned image.'
          : 'That file could not be read.'
      );
      const detail = await api.attachFile(saved.id, currentId.current);
      setCurrent(detail);
      refreshList();
      afterAttach(detail);
    } catch (err) {
      setError(err.message);
    } finally {
      setWorking('');
    }
  }

  async function detach() {
    if (!current?.id) return;
    try {
      setCurrent(await api.detachFile(current.id));
    } catch (err) {
      setError(err.message);
    }
  }

  // Asked from elsewhere (a file in a chat): open it in a fresh conversation.
  const attachRef = useRef(null);
  attachRef.current = (fileId) => { currentId.current = null; attach(fileId); };
  useEffect(() => { if (fileRequest?.id) attachRef.current(fileRequest.id); }, [fileRequest]);

  function afterAttach(detail) {
    if (pendingTool && detail?.id) {
      const { key, label } = pendingTool;
      setPendingTool(null);
      runTool(key, label, detail);
    }
  }

  // The tools are always there. Without a file, they ask which one first.
  async function runTool(tool, label, conv = current) {
    if (pending || working) return;
    if (!conv?.id || !conv.file) {
      setPendingTool({ key: tool, label });
      setAttaching(true);
      return;
    }
    setPending(true);
    setError('');
    setCurrent({ ...conv, messages: [...conv.messages, { id: `local-${Date.now()}`, role: 'user', body: `✦ ${label}` }] });
    try {
      await api.runDocTool(conv.id, tool);
      setCurrent(await api.conversation(conv.id));
      refreshList();
    } catch (err) {
      setError(err.message);
      setCurrent(await api.conversation(conv.id).catch(() => conv));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="assistant">
      <header className="copilot-head">
        <span className="copilot-title"><Icon name="copilot" size={18} /> Copilot</span>
        <IconButton
          icon={showHistory ? 'back' : 'history'}
          label={showHistory ? 'Back to chat' : `History${conversations.length ? ` (${conversations.length})` : ''}`}
          pressed={showHistory}
          onClick={() => setShowHistory((v) => !v)}
        />
        <IconButton icon="compose" label="New conversation" onClick={startNew} />
        <IconButton icon="close" label="Close Copilot" onClick={onClose} />
      </header>

      {showHistory && (
      <aside className="assistant-list">
        <button type="button" className="btn primary assistant-new" onClick={startNew}>
          New conversation
        </button>
        <div className="seg history-filter" role="group" aria-label="Show">
          {[['all', 'All'], ['files', 'With files']].map(([k, l]) => (
            <button key={k} type="button" className={`seg-btn${historyFilter === k ? ' is-on' : ''}`}
              aria-pressed={historyFilter === k} onClick={() => setHistoryFilter(k)}>
              {k === 'files' && <Icon name="file" size={14} />}{l}
            </button>
          ))}
        </div>
        {conversations.length === 0 && (
          <p className="muted-sm assistant-empty-list">No conversations yet.</p>
        )}
        {historyFilter === 'files' && conversations.length > 0 && !conversations.some((c) => c.file_name) && (
          <p className="muted-sm assistant-empty-list">No conversations about files yet — attach one with the paperclip.</p>
        )}
        <ul>
          {conversations.filter((c) => historyFilter === 'all' || c.file_name).map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className={`assistant-item${current?.id === c.id ? ' is-current' : ''}`}
                onClick={() => open(c.id)}
              >
                <span className="assistant-item-title">{c.title}</span>
                {c.file_name && (
                  <span className="assistant-item-file"><Icon name="file" size={13} /> {c.file_name}</span>
                )}
                <span className="assistant-item-meta">{c.message_count} message{c.message_count === 1 ? '' : 's'}</span>
              </button>
              <button
                type="button"
                className="assistant-del"
                title="Delete conversation"
                aria-label={`Delete ${c.title}`}
                onClick={(e) => remove(c.id, e)}
              >
                <Icon name="trash" size={16} />
              </button>
            </li>
          ))}
        </ul>
      </aside>
      )}

      {!showHistory && (
      <section className="assistant-pane">
        <header className="assistant-head">
          <div className="assistant-presets" role="group" aria-label="Answer style">
            {presets.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`seg-btn${preset === p.key ? ' is-on' : ''}`}
                title={p.description}
                aria-pressed={preset === p.key}
                onClick={() => setPreset(p.key)}
              >
                <Icon name={p.key === 'thinking' ? 'think' : 'bolt'} size={16} />
                {p.label}
              </button>
            ))}
          </div>
        </header>

        <div className="assistant-scroll">
          {messages.length === 0 && !pending && file && (
            <div className="assistant-empty">
              <h2>Ask about {file.filename}</h2>
              <p className="muted-sm">
                Try &ldquo;summarise this&rdquo;, &ldquo;what should I watch out for?&rdquo; or
                &ldquo;what are the payment terms?&rdquo; — or use the tools above. Answers cite the
                page they came from.
              </p>
            </div>
          )}
          {messages.length === 0 && !pending && !file && (
            <div className="assistant-empty">
              <h2>Ask anything</h2>
              <p className="muted-sm">
                General questions work as you would expect. It also knows your side of
                Builders&rsquo; Network, and it can do things for you — try
                &ldquo;help me with my profile&rdquo;, &ldquo;what&rsquo;s new in my
                feed?&rdquo; or &ldquo;take me to the news&rdquo;. Attach a file with
                the paperclip to talk about it{imagesOn ? ', or press the picture button to create an image' : ''}.
              </p>
              <p className="muted-sm"><a href="/guide/copilot" target="_blank" rel="noopener noreferrer">New to Copilot? The 2-minute guide →</a></p>
            </div>
          )}
          {messages.map((m) => (
            m.role === 'assistant' && m.body.startsWith(FOUND_MARK) ? (
              <div key={m.id} className="assistant-msg assistant-msg-assistant">
                <Found body={m.body} onNavigate={onNavigate} />
              </div>
            ) : m.role === 'assistant' && m.body.startsWith(IMAGE_MARK) ? (
              <div key={m.id} className="assistant-msg assistant-msg-assistant">
                <GeneratedImage fileId={m.body.slice(IMAGE_MARK.length).split('\n')[0]}
                  prompt={m.body.split('\n').slice(1).join('\n')} busy={pending}
                  onRetry={(p) => sendText(p, true)} />
              </div>
            ) : m.role === 'assistant' && m.body.startsWith(MINDMAP_MARK) ? (
              <div key={m.id} className="assistant-msg assistant-msg-assistant mindmap-msg">
                <MindMap text={m.body.slice(MINDMAP_MARK.length)} title={file?.filename} />
              </div>
            ) : (
              <div key={m.id} className={`assistant-msg assistant-msg-${m.role}`}>
                <Rich text={m.body} />
              </div>
            )
          ))}
          {working && <p className="muted-sm assistant-working">{working}</p>}
          {action && (
            <ActionCard
              action={action}
              onApply={applyAction}
              onDismiss={dismissAction}
              busy={applying}
            />
          )}
          {pending && <Waiting preset={preset} image={pendingImage} />}
          {error && <p className="assistant-error" role="alert">{error}</p>}
          <div ref={endRef} />
        </div>

        <div className="doc-bar">
          {file && (
            <div className="doc-chip">
              <Icon name={file.is_image ? 'image' : 'file'} size={16} />
              <span title={file.filename}>
                {file.is_image ? 'Looking at ' : 'Talking about '}<b>{file.filename}</b>
                {file.pages ? ` · ${file.pages} p.` : ''}
              </span>
              <IconButton icon="close" label="Stop talking about this file" size="sm" onClick={detach} />
            </div>
          )}
          {file?.is_image ? (
            <p className="muted-sm image-hint">
              Ask anything about this image — what it shows, the text in it, what to change.
              Images are answered by the vision model, whichever option is selected.
            </p>
          ) : (
          <div className="doc-tools" role="group" aria-label="Document tools">
              {DOC_TOOLS.map(([key, label, icon]) => (
                <button key={key} type="button" className="doc-tool" disabled={pending || Boolean(working)}
                  title={file ? `${label} of ${file.filename}` : `${label} of a file — you'll pick which`}
                  onClick={() => runTool(key, label)}>
                  <Icon name={icon} size={16} /> {label}
                </button>
              ))}
              {SHOW_PODCAST && (
                <button type="button" className="doc-tool soon"
                  onClick={() => window.alert('Podcast is coming soon: a short audio conversation about your document that you can listen to on the go.')}>
                  <Icon name="podcast" size={16} /> Podcast <em>soon</em>
                </button>
              )}
          </div>
          )}
        </div>

        {attaching && (
          <AttachMenu
            heading={pendingTool ? `${pendingTool.label} — which document?` : ''}
            documentsOnly={Boolean(pendingTool)}
            onPick={attach}
            onUpload={uploadAndAttach}
            onClose={() => { setAttaching(false); setPendingTool(null); }}
          />
        )}

        <form className="assistant-composer" onSubmit={send}>
          <IconButton icon="attach" label="Attach a file or image" onClick={() => { setPendingTool(null); setAttaching((v) => !v); }} pressed={attaching} disabled={pending || Boolean(working)} />
          {!file && imagesOn && <IconButton icon="image" label={imageMode ? 'Creating an image — press to cancel' : 'Create an image'}
            onClick={() => setImageMode((v) => !v)} pressed={imageMode} disabled={pending || Boolean(working)} />}
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) send(e);
            }}
            placeholder={working ? 'Reading your file — ask as soon as it\'s ready…'
              : file ? `Ask about ${file.filename}…`
              : imageMode ? 'Describe the image you want — e.g. a friendly home robot, studio light…'
              : imagesOn ? 'Ask Copilot, attach a file, or create an image…' : 'Ask Copilot, or attach a file…'}
            rows={2}
            disabled={pending}
          />
          <IconButton type="submit" icon="send" label={working ? 'Wait — reading your file' : 'Send'}
            variant="primary" disabled={pending || Boolean(working) || !draft.trim()} />
        </form>
      </section>
      )}
    </div>
  );
}
